/**
 * NMS WebEngage Event Manager
 * Centralized WebEngage event handling for Neemans components
 *
 * Data Structure:
 * - window.neemans.analytics.*       - Analytics data (shared with GTM)
 * - window.neemans.webengage.user    - User data for WebEngage
 * - window.neemans.webengage.product - Product data (on product pages)
 * - window.neemans.webengage.cart    - Cart data (when cart has items)
 * - window.neemans.webengage.events  - This WebEngage manager instance
 *
 * Integrates with existing NMS component system and provides:
 * - User identification and attribute tracking
 * - E-commerce event tracking
 * - Custom business event tracking
 * - Automatic NMS event conversion
 * - Integration with theme system events
 */

class NMSWebEngageEvents {
  constructor() {
    this.webengage = window.webengage;
    this.config = window.neemans?.webengage?.config || {};
    this.debugMode = false;
    this.eventQueue = [];
    this.isInitialized = false;

    // Retry management to prevent infinite recursion
    this.retryCount = 0;
    this.maxRetries = 5;
    this.retryTimeouts = [];
    this.fallbackMode = false;

    // Initialize WebEngage integration
    this.init();
  }

  init() {
    // Set up global namespace
    window.neemans = window.neemans || {};
    window.neemans.webengage = window.neemans.webengage || {};
    window.neemans.webengage.events = this;

    // Wait for WebEngage SDK to be ready
    if (this.webengage && typeof this.webengage.onReady === 'function') {
      this.webengage.onReady(() => {
        this.setupWebEngageConfig();
        this.setupNMSEventListeners();
        this.setupMenuLinkListeners();
        this.processEventQueue();
        this.isInitialized = true;
        this.clearRetryTimeouts(); // Clean up any pending retries
        this.log('WebEngage Event Manager initialized successfully');
      });
    } else {
      // Check retry limit to prevent infinite recursion
      if (this.retryCount >= this.maxRetries) {
        this.log('WebEngage SDK failed to load after maximum retries. Enabling fallback mode.');
        this.enableFallbackMode();
        return;
      }

      // Exponential backoff: 1s, 2s, 4s, 8s, 16s
      const delay = Math.min(1000 * Math.pow(2, this.retryCount), 16000);
      this.retryCount++;

      this.log(`WebEngage SDK not ready. Retry ${this.retryCount}/${this.maxRetries} in ${delay}ms`);

      const timeoutId = setTimeout(() => {
        this.init();
      }, delay);

      // Store timeout ID for cleanup
      this.retryTimeouts.push(timeoutId);
    }
  }

  setupWebEngageConfig() {
    // Configure WebEngage options if needed
    if (this.webengage && typeof this.webengage.options === 'function') {
      this.webengage.options({
        debug: false,
        autoTrack: false, // We handle tracking manually via NMS
      });
    }
  }

  // Clear all pending retry timeouts to prevent memory leaks
  clearRetryTimeouts() {
    this.retryTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.retryTimeouts = [];
  }

  // Enable fallback mode when WebEngage fails to load
  enableFallbackMode() {
    this.fallbackMode = true;
    this.isInitialized = true; // Allow event processing in fallback mode

    // Set up basic event listeners without WebEngage
    this.setupNMSEventListeners();
    this.setupMenuLinkListeners();
    this.processEventQueue();

    this.log('Fallback mode enabled - events will be logged but not sent to WebEngage');
  }

  // Call this method when component needs cleanup
  destroy() {
    this.clearRetryTimeouts();
    this.retryCount = 0;
    this.fallbackMode = false;
    this.isInitialized = false;
  }

  /**
   * Main method to track events with WebEngage
   * @param {string} eventName - WebEngage event name
   * @param {object} eventData - Event data object
   * @param {object} options - Additional options
   */
  trackEvent(eventName, eventData = {}, options = {}) {
    if (!this.isInitialized) {
      this.eventQueue.push({ eventName, eventData, options });
      return;
    }

    // Handle fallback mode - log events but don't send to WebEngage
    if (this.fallbackMode) {
      this.log('Fallback mode - Event would be tracked:', eventName, eventData);
      if (options.callback && typeof options.callback === 'function') {
        options.callback(eventData);
      }
      return;
    }

    if (!this.webengage || typeof this.webengage.track !== 'function') {
      this.log('WebEngage SDK not available - switching to fallback mode');
      this.enableFallbackMode();
      return;
    }

    // Validate required fields
    if (!eventName) {
      return;
    }

    // Add common context data (skip for "Page Viewed" event which has its own schema)
    const enrichedData =
      eventName === 'Page Viewed'
        ? eventData
        : {
            timestamp: new Date().toISOString(),
            'Page Title': document.title,
            'Page URL': window.location.href,
            'Page Type': this.getNeemansData('webengage.page.type'),
            ...eventData,
          };

    // Filter out null, undefined, and empty values to prevent WebEngage warnings
    const cleanedData = {};
    Object.entries(enrichedData).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        cleanedData[key] = value;
      }
    });

    // Track with WebEngage
    this.webengage.track(eventName, cleanedData);
    this.log('WebEngage Event tracked:', eventName, cleanedData);

    // Trigger callback if provided
    if (options.callback && typeof options.callback === 'function') {
      options.callback(enrichedData);
    }
  }

  /**
   * User identification and attribute management
   */

  // Identify user (login)
  identifyUser(userId, userAttributes = {}) {
    if (!this.webengage || typeof this.webengage.user.login !== 'function') {
      this.log('WebEngage SDK not available for user identification');
      return;
    }

    this.webengage.user.login(userId.toString());
    this.log('User identified:', userId);

    // Set user attributes
    if (Object.keys(userAttributes).length > 0) {
      this.setUserAttributes(userAttributes);
    }
  }

  // Set user attributes
  setUserAttributes(attributes) {
    if (!this.webengage || typeof this.webengage.user.setAttribute !== 'function') {
      return;
    }

    Object.entries(attributes).forEach(([key, value]) => {
      this.webengage.user.setAttribute(key, value);
    });

    this.log('User attributes set:', attributes);
  }

  // User logout
  logoutUser() {
    if (this.webengage && typeof this.webengage.user.logout === 'function') {
      this.webengage.user.logout();
      this.log('User logged out');
    }
  }

  /**
   * E-commerce specific events
   */

  // Product view tracking
  trackProductView(product) {
    // Use rich SSR data that matches cart event structure
    const selectedVariant = product.selectedVariant || {};

    this.trackEvent('Product Viewed', {
      // Core product data (matching cart event)
      Color: product.color,
      'Product ID': product.product_id || product.id,
      'Product Name': product.product_name || product.title,
      'Product Price': (selectedVariant.price || product.price || 0) / 100,
      Image: this.#ensureHttps(product.featured_image_url),
      'Image URL': this.#ensureHttps(product.featured_image_url),
      Images: (product.images || []).map(url => this.#ensureHttps(url)),
      'Product Variant ID': selectedVariant.id,
      Size: selectedVariant.size || selectedVariant.title,
      Currency: product.currency || 'USD',
      Discount: selectedVariant.discount || 0,

      // Additional product view specific data
      'Product Category': product.product_category || product.type,
      'Product Vendor': product.product_vendor || product.vendor,
      URL: window.location.href,
      Available: selectedVariant.available !== undefined ? selectedVariant.available : product.available,
      'Compare At Price': (selectedVariant.compareAtPrice || product.compareAtPrice || 0) / 100,

      // Missing fields from live environment
      'Number of reviews': 0, // TODO: Connect to review system data
      'Product For': '', // TODO: Connect to product metadata
      'Product Rating': 0, // TODO: Connect to review system data
      Quantity: 1, // Default quantity for product view
    });
  }

  // Add to cart tracking
  trackAddToCart(variant, quantity = 1) {
    const productData = this.formatVariantData(variant);

    this.trackEvent('Product Added To Cart', {
      // Core product data (matching live environment structure)
      Color: productData.color,
      'Product ID': productData.product_id,
      'Product Name': productData.product_title,
      'Product Price': productData.price / 100,
      Image: this.#ensureHttps(productData.featured_image_url),
      'Image URL': this.#ensureHttps(productData.featured_image_url),
      Images: (productData.all_images || []).map(url => this.#ensureHttps(url)),
      'Product Variant ID': productData.id,
      Quantity: quantity,
      Size: productData.size,
      Currency: productData.currency,
      Discount: productData.discount,

      // Additional fields from live environment
      'Number of reviews': 0, // TODO: Connect to review system data
      'Product For': '', // TODO: Connect to product metadata
      'Product Rating': 0, // TODO: Connect to review system data
      'Product URL': window.location.href,
    });
  }

  // Buy now tracking
  trackBuyNow(product, quantity = 1) {
    // Use same structure as trackProductView for consistency
    const selectedVariant = product.selectedVariant || {};

    this.trackEvent('Buy Now', {
      // Core product data (matching live environment structure)
      Color: product.color,
      'Product ID': product.product_id || product.id,
      'Product Name': product.product_name || product.title,
      'Product Price': (selectedVariant.price || product.price || 0) / 100,
      'Image URL': this.#ensureHttps(product.featured_image_url),
      Images: (product.images || []).map(url => this.#ensureHttps(url)),
      'Product Variant ID': selectedVariant.id,
      Quantity: quantity,
      Size: selectedVariant.size || selectedVariant.title,
      Currency: product.currency || 'USD',
      Discount: selectedVariant.discount || 0,

      // Additional fields from live environment
      'Number of reviews': 0, // TODO: Connect to review system data
      'Product For': '', // TODO: Connect to product metadata
      'Product Rating': 0, // TODO: Connect to review system data
      'Product URL': window.location.href,
    });
  }

  // Remove from cart tracking
  trackRemoveFromCart(variant) {
    const productData = this.formatVariantData(variant);

    this.trackEvent('Product Removed from Cart', {
      // Core product data (matching live environment structure)
      Image: this.#ensureHttps(productData.featured_image_url) || '',
      'Product ID': productData.product_id,
      'Product Name': productData.product_title,
      'Product Price': productData.price / 100,
      'Product Variant ID': productData.id,
    });
  }

  // Cart view tracking
  async trackCartView() {
    try {
      // Fetch fresh cart data from Shopify cart.js API
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (!cart || cart.item_count === 0) {
        this.log('Cart is empty - no tracking');
        return;
      }

      this.trackEvent('Cart Viewed', {
        'Cart Value': cart.total_price / 100,
        'Item Count': cart.item_count,
        Currency: cart.currency || 'INR',
        Items: cart.items.map(item => ({
          'Product ID': item.product_id,
          'Variant ID': item.variant_id || item.id,
          'Product Name': item.product_title,
          Price: item.price / 100,
          Quantity: item.quantity,
        })),
      });
    } catch (error) {
      this.handleError(error, 'Failed to fetch cart data for tracking');

      // Fallback: try SSR data if available
      const cartData = this.getNeemansData('webengage.cart');
      if (cartData) {
        this.trackEvent('Cart Viewed', {
          'Cart Value': cartData.totalPrice / 100,
          'Item Count': cartData.itemCount,
          Currency: cartData.currency || 'INR',
        });
      }
    }
  }

  // Collection view tracking
  trackCollectionView(collection) {
    const collectionData = collection || this.getNeemansData('webengage.collection');

    if (!collectionData) {
      this.log('No collection data available for tracking');
      return;
    }

    this.trackEvent('Category/Collection Viewed', {
      'Collection Id': collectionData.id,
      'Collection Name': collectionData.title,
      'Collection Handle': collectionData.handle,
      'Collection URL': collectionData.url || window.location.href,
      'Collection Product Count': collectionData.productsCount || 0,
      'Sort By': collectionData.sortBy || 'manual',
      'Page Type': 'collection',
    });
  }

  // Page view tracking with full schema
  trackPageView(pageData) {
    const userData = this.getNeemansData('webengage.user');

    this.trackEvent('Page Viewed', {
      'Page URL': pageData.url || window.location.href,
      'Page Title': pageData.title || document.title,
      'Shop Domain': pageData.shopDomain,
      'Page Type': pageData.type,
      'Shop Name': pageData.shopName,
      'Customer Email': userData?.email || null,
      'Page Template': pageData.template,
      Currency: pageData.currency,
      'Customer ID': userData?.id || null,
    });
  }

  /**
   * Custom business events
   */

  // Menu link click tracking
  trackMenuLinkClick(linkData) {
    this.trackEvent('Clicked on Menu Link', {
      'Link Name': linkData.name,
      'Page URL': linkData.url,
    });
  }

  // Checkout started tracking with cart.js API
  async trackCheckoutStarted() {
    try {
      // Fetch fresh cart data from Shopify cart.js API
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const trackingData = {
        'Checkout Method': 'Standard',
        Step: 1,
        'Cart Value': cart.total_price ? cart.total_price / 100 : 0,
        'Item Count': cart.item_count || 0,
        Currency: cart.currency || 'INR',
      };

      // Add product details if available
      if (cart.items && cart.items.length > 0) {
        trackingData['Products'] = cart.items.map(item => ({
          'Product ID': item.product_id,
          'Product Name': item.product_title,
          'Variant ID': item.variant_id,
          Price: item.price / 100,
          Quantity: item.quantity,
          'Line Price': item.line_price / 100,
          'Variant Title': item.variant_title,
          SKU: item.sku,
        }));
      }

      this.trackEvent('Checkout Started Step 1', trackingData);
    } catch (error) {
      this.handleError(error, 'Failed to fetch cart data for checkout tracking');

      // Fallback: track without cart details
      this.trackEvent('Checkout Started Step 1', {
        'Checkout Method': 'Standard',
        Step: 1,
      });
    }
  }

  /**
   * Integration with NMS component events
   */
  setupNMSEventListeners() {
    // Define WebEngage-trackable events
    const trackableEvents = ['buy-now-clicked', 'variant-picker:value-changed'];

    // Listen for specific NMS custom events
    document.addEventListener('nms:buy-now-clicked', event => {
      // Use SSR product data from window.neemans.webengage.product
      const productData = this.getNeemansData('webengage.product');
      const quantity = event.detail.quantity || 1;

      if (productData) {
        this.trackBuyNow(productData, quantity);
      }
    });

    // Theme system event: line-item:change (cart updates/removals)
    document.addEventListener('line-item:change', event => {
      const { cart } = event.detail;

      if (cart.item_count === 0) {
        // Cart is now empty - item was removed
        this.trackEvent('Product Removed from Cart', {
          Image: '',
          'Product ID': 0,
          'Product Name': '',
          'Product Price': 0,
          'Product Variant ID': 0,
        });
      } else {
        // Cart still has items - quantity was updated
        this.trackEvent('Cart Updated', {
          Currency: cart.currency || 'INR',
          Products: cart.items.map(item => ({
            id: item.product_id,
            variant_id: item.variant_id || item.id,
            title: item.product_title,
            price: item.price / 100,
            quantity: item.quantity,
            image: this.#ensureHttps(item.image),
            line_price: item.line_price / 100,
          })),
          Total: cart.total_price / 100,
          'Total Quantity': cart.item_count,
        });
      }
    });

    // Theme system event: variant:add (successful add to cart)
    document.addEventListener('variant:add', async event => {
      if (event.detail?.items?.length > 0) {
        const item = event.detail.items[0]; // First/primary item added
        try {
          // Fetch both images and variant pricing data in parallel
          const [allImages, variantData] = await Promise.all([
            this.fetchProductImages(item.handle),
            this.fetchVariantData(item.id),
          ]);

          // Merge cart data with API data for complete tracking
          const enhancedVariantData = {
            id: item.id,
            product_id: item.product_id,
            title: item.title,
            variant_title: item.variant_title,
            price: item.price,
            product_title: item.product_title,
            image: this.#ensureHttps(item.image),
            handle: item.handle,
            all_images: allImages,
            // Add pricing data from variant API
            compare_at_price: variantData?.compare_at_price || 0,
          };

          this.trackAddToCart(enhancedVariantData, item.quantity || 1);
        } catch (error) {
          // Fallback: send basic data if API calls fail
          this.trackAddToCart(
            {
              id: item.id,
              product_id: item.product_id,
              title: item.title,
              variant_title: item.variant_title,
              price: item.price,
              product_title: item.product_title,
              image: this.#ensureHttps(item.image),
              all_images: [item.image], // Fallback to single image
              compare_at_price: 0, // No discount data available
            },
            item.quantity || 1
          );
        }
      }
    });

    // Generic handler for other trackable events
    trackableEvents.forEach(eventName => {
      document.addEventListener(`nms:${eventName}`, event => {
        this.trackEvent('User Interaction', {
          action: eventName,
          category: 'nms_component',
          detail: event.detail,
        });
      });
    });

    this.log('NMS event listeners set up');
  }

  /**
   * Set up menu link click tracking
   * Tracks links with data-webengage-track-link attribute
   */
  setupMenuLinkListeners() {
    document.addEventListener('click', event => {
      // Track menu links
      const link = event.target.closest('[data-webengage-track-link]');
      if (link) {
        const linkData = {
          name: link.textContent.trim(), // Get text automatically
          url: link.href, // Get URL automatically
        };
        this.trackMenuLinkClick(linkData);
        return;
      }

      // Track checkout button clicks
      const checkoutButton = event.target.closest('[data-nms-cart-drawer-checkout-button]');
      if (checkoutButton) {
        this.trackCheckoutStarted();
        return;
      }

      // Track view cart button clicks
      const viewCartButton = event.target.closest('[data-nms-gtm-view-cart]');
      if (viewCartButton) {
        this.trackCartView();
        return;
      }
    });

    this.log('Menu link listeners set up');
  }

  /**
   * Data formatting utilities
   */

  formatProductData(product) {
    return {
      id: product.id,
      title: product.title,
      type: product.type || 'Unknown',
      vendor: product.vendor || 'Unknown',
      price: product.price || product.selected_or_first_available_variant?.price || 0,
      available: product.available !== undefined ? product.available : true,
      currency: this.getNeemansData('analytics.shop.currency') || 'USD',
    };
  }

  formatVariantData(variant) {
    // Extract product title and color from product title format "Cork Slides : Black"
    const productTitle = variant.product_title || variant.untranslated_product_title || '';
    // Extract color ONLY from product_title, not from title (which includes size)
    const color = productTitle.includes(' : ') ? productTitle.split(' : ')[1] : '';

    // Extract size from variant_title (e.g., "UK 10")
    const size = variant.variant_title || variant.untranslated_variant_title || '';

    // Calculate discount percentage if compare_at_price exists
    const price = variant.price || variant.final_price || 0;
    const compareAtPrice = variant.compare_at_price || variant.original_price || 0;
    const discountAmount = compareAtPrice > price ? compareAtPrice - price : 0;
    const discountPercentage = compareAtPrice > 0 ? Math.round((discountAmount / compareAtPrice) * 100) : 0;

    return {
      // Core IDs
      id: variant.id || variant.variant_id,
      product_id: variant.product_id,

      // Product info
      product_title: productTitle,
      variant_title: size,
      price: price,
      compare_at_price: compareAtPrice,
      discount: discountPercentage,

      // Product attributes
      color: color,
      size: size,
      product_type: variant.product_type || 'Unknown',
      vendor: variant.vendor || '',
      sku: variant.sku || '',

      // Images
      featured_image_url: this.#ensureHttps(variant.image || variant.featured_image?.url || ''),

      // All images (from API fetch)
      all_images: (variant.all_images || []).map(url => this.#ensureHttps(url)),

      // Product details
      product_description: variant.product_description || '',
      product_handle: variant.handle || '',
      url: variant.url || '',

      // Pricing and inventory
      quantity: variant.quantity || 1,

      // Context
      currency: this.getNeemansData('analytics.shop.currency') || 'USD',
    };
  }

  // Add https protocol to image URLs
  #ensureHttps(url) {
    if (!url) return url;
    if (url.startsWith('//')) {
      return `https:${url}`;
    }
    return url;
  }

  // Fetch all product images from handle
  async fetchProductImages(handle) {
    if (!handle) return [];

    try {
      // Fetch product data from Shopify API
      const product = await this.fetchData(`/products/${handle}.js`);

      // Extract image URLs and ensure https
      const images = product.images || [];
      return images.slice(0, 6).map(url => this.#ensureHttps(url));
    } catch (error) {
      this.handleError(error, `Failed to fetch images for ${handle}`);
      return []; // Return empty array on error
    }
  }

  // Fetch variant data with pricing information
  async fetchVariantData(variantId) {
    if (!variantId) return null;

    try {
      // Fetch variant data from Shopify API
      const variant = await this.fetchData(`/variants/${variantId}.js`);
      return variant;
    } catch (error) {
      this.handleError(error, `Failed to fetch variant data for ${variantId}`);
      return null; // Return null on error
    }
  }

  formatLineItemData(lineItem) {
    return {
      product_id: lineItem.product_id,
      variant_id: lineItem.variant_id,
      product_name: lineItem.title,
      variant_name: lineItem.variant_title,
      price: lineItem.price / 100,
      quantity: lineItem.quantity,
      currency: this.getNeemansData('analytics.shop.currency') || 'USD',
    };
  }

  /**
   * Utility methods
   */

  // Fetch data from Shopify API
  async fetchData(url) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.json();
  }

  // Handle errors with logging
  handleError(error, context = '') {
    const message = context ? `${context}: ${error.message}` : error.message;
    console.error('[NMS WebEngage Error]', message);
    if (this.debugMode) {
      console.error('[NMS WebEngage Error Details]', error);
    }
  }

  // Get data from window.neemans namespace
  getNeemansData(key) {
    const keys = key.split('.');
    let data = window.neemans;

    for (const k of keys) {
      data = data?.[k];
      if (data === undefined) return null;
    }

    return data;
  }

  processEventQueue() {
    while (this.eventQueue.length > 0) {
      const { eventName, eventData, options } = this.eventQueue.shift();
      this.trackEvent(eventName, eventData, options);
    }
  }

  log(...args) {
    if (this.debugMode) {
      console.log('[NMS WebEngage]', ...args);
    }
  }
}

// Initialize WebEngage Event Manager when DOM is ready
function initNMSWebEngageEvents() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new NMSWebEngageEvents();
    });
  } else {
    new NMSWebEngageEvents();
  }
}

// Auto-initialize
initNMSWebEngageEvents();
